import { Helmet } from 'react-helmet-async';

// Organization Schema
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Materialidtf.it",
    "alternateName": "Materiali DTF",
    "url": "https://materialidtf.it",
    "logo": "https://materialidtf.it/logo-materialidtf.png",
    "description": "Fornitore leader di materiali per stampa DTF professionale in Italia. Inchiostri, bobine, colla, ricambi e assistenza tecnica specializzata.",
    "foundingDate": "2020",
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+39-375-7190556",
        "contactType": "customer service",
        "availableLanguage": ["Italian", "English"],
        "areaServed": "IT"
      },
      {
        "@type": "ContactPoint",
        "telephone": "+39-375-7190556",
        "contactType": "technical support",
        "availableLanguage": ["Italian"]
      }
    ],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Via Don Minzoni 15",
      "addressLocality": "Fonte Nuova",
      "addressRegion": "RM",
      "postalCode": "00013",
      "addressCountry": "IT"
    },
    "sameAs": [
      "https://www.facebook.com/materialidtf",
      "https://www.instagram.com/materialidtf",
      "https://www.linkedin.com/company/materialidtf"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// WebSite Schema with SearchAction
export const WebSiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Materialidtf.it",
    "url": "https://materialidtf.it",
    "description": "Materiali per stampa DTF professionale: inchiostri, bobine, colla e ricambi. Assistenza tecnica e profilatura ICC.",
    "inLanguage": "it-IT",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://materialidtf.it/categoria/{search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// LocalBusiness Schema
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Materialidtf.it",
    "image": "https://materialidtf.it/logo-materialidtf.png",
    "url": "https://materialidtf.it",
    "telephone": "+39-375-7190556",
    "email": "info@materialidtf.it",
    "priceRange": "€€",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Via Don Minzoni 15",
      "addressLocality": "Fonte Nuova",
      "addressRegion": "RM",
      "postalCode": "00013",
      "addressCountry": "IT"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Materiali per Stampa DTF",
      "itemListElement": [
        {
          "@type": "OfferCatalog",
          "name": "Inchiostri DTF"
        },
        {
          "@type": "OfferCatalog",
          "name": "Bobine e Film DTF"
        },
        {
          "@type": "OfferCatalog",
          "name": "Colla e Polvere DTF"
        },
        {
          "@type": "OfferCatalog",
          "name": "Ricambi Stampanti"
        }
      ]
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Service Schema for service pages
interface ServiceSchemaProps {
  name: string;
  description: string;
  url: string;
  provider?: string;
}

export const ServiceSchema = ({ name, description, url, provider = "Materialidtf.it" }: ServiceSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "url": `https://materialidtf.it${url}`,
    "provider": {
      "@type": "Organization",
      "name": provider,
      "url": "https://materialidtf.it"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Italy"
    },
    "serviceType": "Servizi Stampa DTF"
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// BreadcrumbList Schema
interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export const BreadcrumbSchema = ({ items }: BreadcrumbSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://materialidtf.it${item.url}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Product Schema
interface ProductSchemaProps {
  name: string;
  description: string;
  image: string;
  price: number;
  currency?: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  sku?: string;
  brand?: string;
}

export const ProductSchema = ({ 
  name, 
  description, 
  image, 
  price, 
  currency = 'EUR',
  availability = 'InStock',
  sku,
  brand = 'Materialidtf'
}: ProductSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": name,
    "description": description,
    "image": image,
    "brand": {
      "@type": "Brand",
      "name": brand
    },
    ...(sku && { "sku": sku }),
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": currency,
      "availability": `https://schema.org/${availability}`,
      "seller": {
        "@type": "Organization",
        "name": "Materialidtf.it"
      }
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// FAQPage Schema
interface FAQItem {
  question: string;
  answer: string;
}

interface FAQPageSchemaProps {
  items: FAQItem[];
}

export const FAQPageSchema = ({ items }: FAQPageSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

